/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/quickEntryParser.ts
 * PURPOSE: Modal [5] "Nhập nhanh bằng văn bản" (BR-SCHD-011) — bộ phân tích
 *          chuỗi PHÍA TRÌNH DUYỆT, tách khỏi phần ghi lịch (backend chỉ nhận
 *          danh sách assignment đã parse — xem manager_schedule.py
 *          quick_entry_schedule()). Pure functions, không side-effect, không
 *          gọi API — mọi dữ liệu tra cứu (roster + danh mục mã ca) đã có sẵn
 *          từ chính lưới đang mở, đúng ý đồ wireframe.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-07
 * COMPLIANCE: SDLC 6.5.0 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Bảng phân ca (SC-009), kênh nhập ca bằng văn bản
 * - Impact: Data Integrity — khớp sai NV/mã ca hoặc bỏ sót [R42] tạo ca sai
 *   hàng loạt qua một thao tác dán văn bản
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (pure parser, unit-testable độc lập UI)
 * - Pattern: pure functions, no side effects
 * - Dependencies: scheduleShared.noDiacritics
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-009-SCHEDULE-GRID ANNOTATION "[5] Nhập nhanh bằng văn bản" (dòng ~3427)
 *   + [R42] (dòng ~3444-3452, đặc biệt: "Một dòng chỉ bị loại toàn bộ khi
 *   không phân tích được cú pháp hoặc mã nhân viên không khớp" — mã ca không
 *   khớp danh mục CHỈ loại đúng GIÁ TRỊ đó, không loại cả dòng, cùng cách xử
 *   lý với "Ngày đã qua")
 * - UI: frontend/src/pages/manager/schedule/QuickEntryModal.tsx
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py (quick_entry_schedule)
 * ===============================================================================
 */

import { noDiacritics } from "./scheduleShared";

export interface QuickEntryRosterEmployee {
  user_id: string;
  full_name: string;
  employee_code: string | null;
}

export interface QuickEntryShiftCode {
  id: string;
  code: string | null;
  start_time: string; // "HH:MM"
}

/** 1 mã ca trong 1 giá trị (tách bằng dấu `+` khi 1 ngày nhiều ca) */
export interface QuickEntryValueCode {
  codeRaw: string;
  matched: QuickEntryShiftCode | null;
  /** null = sẽ được gán; khác null = LOẠI khỏi phần gán (KHÔNG loại cả dòng) */
  excludedReason: "unknown_code" | "past_r42" | null;
}

/** 1 ngày trong 1 dòng (giá trị thứ i ứng với ngày thứ i của khoảng) */
export interface QuickEntryValue {
  dateISO: string;
  raw: string;
  isOff: boolean;
  codes: QuickEntryValueCode[];
}

export interface QuickEntryLine {
  lineNumber: number; // 1-based, theo thứ tự dòng KHÔNG RỖNG trong ô dán
  raw: string;
  employeeCodeRaw: string;
  matchedEmployee: QuickEntryRosterEmployee | null;
  values: QuickEntryValue[];
  /** khác null → LOẠI TOÀN BỘ dòng (không phân tích được cú pháp / mã NV không khớp) */
  error: string | null;
  /** dòng hợp lệ nhưng bị 1 dòng SAU cùng mã NV ghi đè — "bị thay bởi dòng bên dưới" */
  supersededByLaterDuplicate: boolean;
}

export interface QuickEntryAssignmentDraft {
  user_id: string;
  date: string;
  shift_template_id: string;
}

export interface QuickEntryParseResult {
  lines: QuickEntryLine[];
  validLineCount: number;
  errorLineCount: number;
  assignments: QuickEntryAssignmentDraft[];
  assignedCount: number;
  /** verbatim NHÃN VI: "✔ {N} dòng hợp lệ ({M} phân ca) sẽ gán · ✖ {K} dòng lỗi" */
  summary: string;
}

export interface QuickEntryParseOptions {
  employees: QuickEntryRosterEmployee[];
  shiftCodes: QuickEntryShiftCode[];
  /** danh sách ngày ISO theo đúng thứ tự của khoảng áp dụng — length = N giá trị bắt buộc/dòng */
  dates: string[];
  /** mốc so R42 (mặc định new Date()) — tham số hoá để test được xác định */
  now?: Date;
}

const REASON_EMPLOYEE_NOT_FOUND = "Không tìm thấy mã nhân viên";
const REASON_UNKNOWN_SHIFT_CODE = "Mã ca không có trong danh mục";
const REASON_PAST_R42 = "Ngày đã qua";
const REASON_SUPERSEDED = "bị thay bởi dòng bên dưới";

function reasonValueCount(actual: number, expected: number): string {
  return `Dòng có ${actual} giá trị, cần đúng ${expected} (theo thứ tự ngày của khoảng).`;
}

/** `ngày + giờ bắt đầu` ≤ mốc so → khoá (cùng công thức backend _is_write_locked_by_r42, tới phút, không vùng đệm) */
function isPastR42(dateISO: string, startTimeHHMM: string, now: Date): boolean {
  const [hh, mm] = startTimeHHMM.split(":").map((v) => parseInt(v, 10));
  const [yyyy, mo, dd] = dateISO.split("-").map((v) => parseInt(v, 10));
  const target = new Date(yyyy, (mo || 1) - 1, dd || 1, hh || 0, mm || 0, 0, 0);
  return now.getTime() >= target.getTime();
}

function findEmployee(
  codeRaw: string,
  employees: QuickEntryRosterEmployee[],
): QuickEntryRosterEmployee | null {
  const trimmed = codeRaw.trim();
  if (!trimmed) return null;
  const byCode = employees.find(
    (e) => e.employee_code != null && e.employee_code.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  if (byCode) return byCode;
  const normalized = noDiacritics(trimmed).trim();
  const byName = employees.find((e) => noDiacritics(e.full_name).trim() === normalized);
  return byName ?? null;
}

function findShiftCode(codeRaw: string, shiftCodes: QuickEntryShiftCode[]): QuickEntryShiftCode | null {
  const trimmed = codeRaw.trim();
  if (!trimmed) return null;
  return (
    shiftCodes.find((s) => s.code != null && s.code.trim().toLowerCase() === trimmed.toLowerCase()) ?? null
  );
}

/**
 * Phân tích toàn bộ nội dung ô dán "Nhập nhanh" thành các dòng đã khớp NV/mã
 * ca + danh sách assignment sẽ gửi lên backend. KHÔNG gọi API — mọi tra cứu
 * dùng `employees`/`shiftCodes` đã tải sẵn (từ lưới đang mở).
 */
export function parseQuickEntryText(
  text: string,
  opts: QuickEntryParseOptions,
): QuickEntryParseResult {
  const { employees, shiftCodes, dates } = opts;
  const now = opts.now ?? new Date();
  const rawLines = text.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);

  const lines: QuickEntryLine[] = rawLines.map((raw, idx) => {
    const lineNumber = idx + 1;
    const sepIdx = raw.indexOf(":");
    if (sepIdx < 0) {
      return {
        lineNumber,
        raw,
        employeeCodeRaw: raw,
        matchedEmployee: null,
        values: [],
        error: REASON_EMPLOYEE_NOT_FOUND,
        supersededByLaterDuplicate: false,
      };
    }
    const employeeCodeRaw = raw.slice(0, sepIdx).trim();
    const valuesRaw = raw.slice(sepIdx + 1);
    const matchedEmployee = findEmployee(employeeCodeRaw, employees);
    if (!matchedEmployee) {
      return {
        lineNumber,
        raw,
        employeeCodeRaw,
        matchedEmployee: null,
        values: [],
        error: REASON_EMPLOYEE_NOT_FOUND,
        supersededByLaterDuplicate: false,
      };
    }

    const tokens = valuesRaw.split(",").map((t) => t.trim());
    if (tokens.length !== dates.length) {
      return {
        lineNumber,
        raw,
        employeeCodeRaw,
        matchedEmployee,
        values: [],
        error: reasonValueCount(tokens.length, dates.length),
        supersededByLaterDuplicate: false,
      };
    }

    const values: QuickEntryValue[] = tokens.map((tokenRaw, dayIdx) => {
      const dateISO = dates[dayIdx];
      const isOff = tokenRaw.toUpperCase() === "OFF" || tokenRaw === "";
      if (isOff) {
        return { dateISO, raw: tokenRaw, isOff: true, codes: [] };
      }
      const codeTokens = tokenRaw.split("+").map((c) => c.trim()).filter((c) => c.length > 0);
      const codes: QuickEntryValueCode[] = codeTokens.map((codeRaw) => {
        const matched = findShiftCode(codeRaw, shiftCodes);
        if (!matched) {
          return { codeRaw, matched: null, excludedReason: "unknown_code" as const };
        }
        if (isPastR42(dateISO, matched.start_time, now)) {
          return { codeRaw, matched, excludedReason: "past_r42" as const };
        }
        return { codeRaw, matched, excludedReason: null };
      });
      return { dateISO, raw: tokenRaw, isOff: false, codes };
    });

    return {
      lineNumber,
      raw,
      employeeCodeRaw,
      matchedEmployee,
      values,
      error: null,
      supersededByLaterDuplicate: false,
    };
  });

  // Dòng trùng mã NV → dòng SAU thắng, dòng trước đánh dấu "bị thay bởi dòng bên dưới"
  const lastIndexByUser = new Map<string, number>();
  lines.forEach((line, idx) => {
    if (line.error || !line.matchedEmployee) return;
    lastIndexByUser.set(line.matchedEmployee.user_id, idx);
  });
  lines.forEach((line, idx) => {
    if (line.error || !line.matchedEmployee) return;
    if (lastIndexByUser.get(line.matchedEmployee.user_id) !== idx) {
      line.supersededByLaterDuplicate = true;
    }
  });

  const assignments: QuickEntryAssignmentDraft[] = [];
  lines.forEach((line) => {
    if (line.error || !line.matchedEmployee || line.supersededByLaterDuplicate) return;
    line.values.forEach((value) => {
      if (value.isOff) return;
      value.codes.forEach((code) => {
        if (code.matched && code.excludedReason === null) {
          assignments.push({
            user_id: line.matchedEmployee!.user_id,
            date: value.dateISO,
            shift_template_id: code.matched.id,
          });
        }
      });
    });
  });

  const validLineCount = lines.filter((l) => !l.error).length;
  const errorLineCount = lines.filter((l) => l.error).length;
  const assignedCount = assignments.length;
  const summary = `✔ ${validLineCount} dòng hợp lệ (${assignedCount} phân ca) sẽ gán · ✖ ${errorLineCount} dòng lỗi`;

  return { lines, validLineCount, errorLineCount, assignments, assignedCount, summary };
}

export const QUICK_ENTRY_REASONS = {
  EMPLOYEE_NOT_FOUND: REASON_EMPLOYEE_NOT_FOUND,
  UNKNOWN_SHIFT_CODE: REASON_UNKNOWN_SHIFT_CODE,
  PAST_R42: REASON_PAST_R42,
  SUPERSEDED: REASON_SUPERSEDED,
} as const;
